/**
 * AIIntakeChat — the core AI intake conversation UI (Quiet Trust patient theme).
 *
 * Orchestrates:
 *   - useAIIntake hook (session management, SSE streaming)
 *   - MessageBubble (chat messages)
 *   - FieldTracker (field collection progress)
 *   - InlineSignaturePad (when signatureRequired is emitted)
 *   - SSN masked input (when ssnRequired is emitted)
 *   - ConfirmationScreen (after <complete /> is emitted)
 *
 * Props:
 *   formId         — the form schema ID
 *   assignmentId   — optional assignment ID (for assigned forms)
 *   locationId     — tenant location ID
 *   formFields     — flattened list of form fields (for FieldTracker + ConfirmationScreen)
 *   onSubmit       — called with final data after patient confirms
 *   onSwitchToForm — if provided, shows "Switch to form" button (toggle mode)
 *   authToken      — optional contact JWT (assigned-form path)
 *   grant          — optional public form grant (public-form path)
 *   contactName    — optional patient name for personalised greeting
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAIIntake } from '../../hooks/useAIIntake.js';
import MessageBubble from './MessageBubble.js';
import FieldTracker from './FieldTracker.js';
import ConfirmationScreen from './ConfirmationScreen.js';
import InlineSignaturePad from '../signature/InlineSignaturePad.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FormFieldDef {
  key: string;
  label: string;
  type: string;
  required: boolean;
}

interface Props {
  formId: string;
  assignmentId?: string | undefined;
  locationId: string;
  formFields: FormFieldDef[];
  onSubmit: (data: Record<string, unknown>) => void;
  onSwitchToForm?: ((collectedData: Record<string, unknown>) => void) | undefined;
  authToken?: string | undefined;
  grant?: string | undefined;
  /** Form-package step id (pins the AI session to a run item). */
  packageRunItemId?: string | undefined;
  /** Run grant for public-package steps (sent as X-Run-Grant). */
  runGrant?: string | undefined;
  contactName?: string | undefined;
  tenantName?: string | undefined;
}

type ViewMode = 'chat' | 'confirmation' | 'submitted';

// ---------------------------------------------------------------------------
// Inline SVG icons (no emoji)
// ---------------------------------------------------------------------------

function LockIconWhite(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function LockIconMuted(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ci-text-muted)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function SendArrowIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function CheckIconWhite(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function DismissIcon(): React.ReactElement {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c0392b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AIIntakeChat({
  formId,
  assignmentId,
  locationId,
  formFields,
  onSubmit,
  onSwitchToForm,
  authToken,
  grant,
  packageRunItemId,
  runGrant,
  contactName,
  tenantName,
}: Props): React.ReactElement {
  const [viewMode,      setViewMode]      = useState<ViewMode>('chat');
  const [inputText,     setInputText]     = useState('');
  const [signature,     setSignature]     = useState<string | undefined>(undefined);
  const [ssn,           setSsn]           = useState('');
  const [isSubmitting,  setIsSubmitting]  = useState(false);
  const messagesEndRef  = useRef<HTMLDivElement>(null);
  const inputRef        = useRef<HTMLTextAreaElement>(null);

  const {
    messages,
    collectedFields,
    isComplete,
    isTyping,
    isStarting,
    error,
    sessionId,
    collectedCount,
    totalRequired,
    signatureRequired,
    ssnRequired,
    startSession,
    sendMessage,
    confirmSubmit,
    dismissError,
  } = useAIIntake({ formId, assignmentId, locationId, contactName, authToken, grant, packageRunItemId, runGrant });

  // Start session on mount (or restore from sessionStorage)
  useEffect(() => {
    void startSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Switch to confirmation when AI signals completion
  useEffect(() => {
    if (isComplete && viewMode === 'chat') {
      setViewMode('confirmation');
    }
  }, [isComplete, viewMode]);

  const handleSend = useCallback(() => {
    const text = inputText.trim();
    if (!text || isTyping || !sessionId) return;
    sendMessage(text);
    setInputText('');
    inputRef.current?.focus();
  }, [inputText, isTyping, sessionId, sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleConfirm = useCallback(async (confirmedData: Record<string, unknown>) => {
    setIsSubmitting(true);
    const result = await confirmSubmit(confirmedData, signature, ssn || undefined);
    setIsSubmitting(false);
    if (result) {
      setViewMode('submitted');
      // Package steps: /complete deferred insertion and returned the merged data
      // (incl. signature/ssn) — forward THAT so the caller's forms/submit carries
      // everything. Single-form sessions already inserted; onSubmit ignores the arg.
      onSubmit(result.deferred && result.formData ? result.formData : confirmedData);
    }
  }, [confirmSubmit, signature, ssn, onSubmit]);

  // FieldTracker data
  const trackerFields = formFields
    .filter((f) => f.required && f.type !== 'signature' && !f.key.toLowerCase().includes('ssn'))
    .map((f) => ({
      key:       f.key,
      label:     f.label,
      collected: collectedFields[f.key] !== undefined && collectedFields[f.key] !== null && collectedFields[f.key] !== '',
      value:     collectedFields[f.key],
    }));

  const inputDisabled =
    isTyping ||
    !sessionId ||
    (signatureRequired && !signature) ||
    ssnRequired ||
    isComplete;

  // ---------------------------------------------------------------------------
  // Render: submitted
  // ---------------------------------------------------------------------------

  if (viewMode === 'submitted') {
    return (
      <div className="ci-portal ci-enter" style={styles.thankYou}>
        <div style={styles.thankYouIcon}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--ci-primary)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 style={styles.tyTitle}>Thank you</h2>
        <p style={styles.tySub}>Your intake has been submitted securely. Your care team has been notified and will review your information before your appointment.</p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render: confirmation
  // ---------------------------------------------------------------------------

  if (viewMode === 'confirmation') {
    return (
      <ConfirmationScreen
        collectedFields={collectedFields}
        fieldDefs={formFields}
        onConfirm={handleConfirm}
        onBack={() => setViewMode('chat')}
        isSubmitting={isSubmitting}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // Render: chat
  // ---------------------------------------------------------------------------

  return (
    <div className="ci-portal" style={styles.chatWrapper}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <LockIconWhite />
          <span style={styles.headerTitle}>
            Secure Intake{tenantName ? ` — ${tenantName}` : ''}
          </span>
        </div>
        {onSwitchToForm && (
          <button style={styles.switchBtn} onClick={() => onSwitchToForm(collectedFields)}>
            Switch to form
          </button>
        )}
      </div>

      {/* Message area (aria-live region for streamed assistant text) */}
      <div
        style={styles.messages}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
        aria-atomic="false"
        aria-label="Intake conversation"
      >
        {/* Loading skeleton */}
        {isStarting && messages.length === 0 && (
          <div style={styles.skeletonWrap}>
            <div className="ci-skeleton" style={{ ...styles.skeletonBubble, width: '62%' }} />
            <div className="ci-skeleton" style={{ ...styles.skeletonBubble, width: '46%' }} />
            <span style={styles.loadingText}>Setting up your secure session</span>
          </div>
        )}

        {/* Messages */}
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            role={msg.role}
            content={msg.content}
            timestamp={msg.timestamp}
          />
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <MessageBubble
            key="typing"
            role="assistant"
            content=""
            timestamp={new Date()}
            isTyping
          />
        )}

        {/* Field progress tracker */}
        {totalRequired > 0 && messages.length > 1 && (
          <FieldTracker
            fields={trackerFields}
            isComplete={isComplete}
            collectedCount={collectedCount}
            totalRequired={totalRequired}
          />
        )}

        {/* Inline signature pad */}
        {signatureRequired && !signature && (
          <InlineSignaturePad
            onSign={(b64) => {
              setSignature(b64);
              sendMessage('I have signed the document.');
            }}
          />
        )}

        {/* Inline SSN input */}
        {ssnRequired && (
          <SSNInput
            value={ssn}
            onChange={setSsn}
            onSubmit={() => {
              if (ssn.replace(/\D/g, '').length === 9) {
                sendMessage('I have entered my SSN securely.');
              }
            }}
          />
        )}

        {/* Error banner */}
        {error && (
          <div style={styles.errorBanner} role="alert">
            <span>{error}</span>
            <button style={styles.errorDismiss} onClick={dismissError} aria-label="Dismiss error">
              <DismissIcon />
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div style={styles.inputArea}>
        <textarea
          ref={inputRef}
          style={{
            ...styles.input,
            opacity: inputDisabled ? 0.6 : 1,
          }}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            signatureRequired && !signature
              ? 'Please sign above to continue...'
              : ssnRequired
              ? 'Please enter your SSN above to continue...'
              : isComplete
              ? 'Review your information above...'
              : 'Type your response...'
          }
          disabled={inputDisabled}
          rows={2}
          aria-label="Type your response"
        />
        <button
          className="ci-send-btn"
          style={{
            ...styles.sendBtn,
            opacity: (!inputText.trim() || inputDisabled) ? 0.5 : 1,
          }}
          onClick={handleSend}
          disabled={!inputText.trim() || inputDisabled}
        >
          Send
          <SendArrowIcon />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SSN masked input (inline) — neutral teal secure treatment
// ---------------------------------------------------------------------------

function SSNInput({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
}): React.ReactElement {
  const formatSSN = (raw: string): string => {
    const d = raw.replace(/\D/g, '').slice(0, 9);
    if (d.length <= 3) return d;
    if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  };

  const digits = value.replace(/\D/g, '');

  return (
    <div style={ssnStyles.container}>
      <p style={ssnStyles.label}>Please enter your Social Security Number:</p>
      <div style={ssnStyles.row}>
        <input
          className="ci-focusable"
          type="password"
          style={ssnStyles.input}
          value={value}
          onChange={(e) => onChange(formatSSN(e.target.value))}
          placeholder="XXX-XX-XXXX"
          maxLength={11}
          inputMode="numeric"
          autoComplete="off"
          aria-label="Social Security Number"
        />
        <button
          style={{ ...ssnStyles.btn, opacity: digits.length !== 9 ? 0.5 : 1 }}
          disabled={digits.length !== 9}
          onClick={onSubmit}
        >
          Submit
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </button>
      </div>
      <p style={ssnStyles.hint}>
        <LockIconMuted />
        Encrypted — never stored in plain text
      </p>
    </div>
  );
}

const ssnStyles: Record<string, React.CSSProperties> = {
  container: {
    background:   'var(--ci-primary-light)',
    border:       '1px solid var(--ci-border-light)',
    borderRadius: 'var(--ci-radius-lg)',
    padding:      '12px 14px',
    margin:       '4px 0',
  },
  label: { margin: '0 0 8px', fontSize: 13, color: 'var(--ci-text-body)', fontWeight: 500, fontFamily: 'var(--ci-font)' },
  row:   { display: 'flex', gap: 8 },
  input: {
    flex:          1,
    minHeight:     44,
    padding:       '11px 12px',
    border:        '1px solid var(--ci-border)',
    borderRadius:  'var(--ci-radius)',
    fontSize:      16,
    letterSpacing: 4,
    textAlign:     'center',
    fontFamily:    'var(--ci-font)',
    color:         'var(--ci-text)',
    boxSizing:     'border-box',
    outline:       'none',
  },
  btn: {
    display:        'inline-flex',
    alignItems:     'center',
    gap:            6,
    minHeight:      44,
    padding:        '11px 16px',
    background:     'var(--ci-primary)',
    color:          '#fff',
    border:         'none',
    borderRadius:   'var(--ci-radius)',
    fontSize:       13,
    fontWeight:     600,
    cursor:         'pointer',
    fontFamily:     'var(--ci-font)',
  },
  hint: { display: 'flex', alignItems: 'center', gap: 5, margin: '6px 0 0', fontSize: 11, color: 'var(--ci-text-muted)', fontFamily: 'var(--ci-font)' },
};

// ---------------------------------------------------------------------------
// Main styles
// ---------------------------------------------------------------------------

const styles: Record<string, React.CSSProperties> = {
  chatWrapper: {
    display:       'flex',
    flexDirection: 'column',
    background:    '#fff',
    borderRadius:  'var(--ci-radius-lg)',
    boxShadow:     '0 1px 3px rgba(26,39,68,0.06), 0 4px 16px rgba(26,39,68,0.05)',
    overflow:      'hidden',
    height:        '100%',
    minHeight:     480,
    width:         '100%',
    fontFamily:    'var(--ci-font)',
  },
  header: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '10px 16px',
    background:     '#1a2744',
    color:          'rgba(255,255,255,0.92)',
    flexShrink:     0,
  },
  headerLeft:  { display: 'flex', alignItems: 'center', gap: 6 },
  headerTitle: { fontSize: 13, fontWeight: 600 },
  switchBtn:   {
    padding:      '6px 12px',
    background:   'rgba(255,255,255,0.18)',
    border:       '1px solid rgba(255,255,255,0.4)',
    borderRadius: 'var(--ci-radius)',
    color:        '#fff',
    fontSize:     12,
    cursor:       'pointer',
    fontFamily:   'var(--ci-font)',
  },
  messages: {
    flex:          1,
    overflowY:     'auto',
    padding:       '16px 16px 8px',
    display:       'flex',
    flexDirection: 'column',
  },
  skeletonWrap: {
    display:       'flex',
    flexDirection: 'column',
    gap:           10,
    padding:       '8px 0',
  },
  skeletonBubble: {
    height:       38,
    borderRadius: 'var(--ci-radius-lg)',
    borderBottomLeftRadius: 4,
    background:   'var(--ci-border-light)',
  },
  loadingText: { fontSize: 13, color: 'var(--ci-text-muted)', fontFamily: 'var(--ci-font)', marginTop: 2 },
  errorBanner: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    background:     '#fef4f2',
    border:         '1px solid #f5c6bc',
    borderRadius:   'var(--ci-radius)',
    padding:        '8px 12px',
    fontSize:       13,
    color:          '#c0392b',
    margin:         '4px 0 8px',
    fontFamily:     'var(--ci-font)',
  },
  errorDismiss: {
    display:    'inline-flex',
    alignItems: 'center',
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    padding:    0,
  },
  inputArea: {
    display:    'flex',
    gap:        8,
    padding:    '10px 12px',
    borderTop:  '1px solid var(--ci-border-light)',
    background: 'var(--ci-surface)',
    flexShrink: 0,
    alignItems: 'flex-end',
  },
  input: {
    flex:        1,
    padding:     '11px 12px',
    minHeight:   46,
    border:      '1px solid var(--ci-border)',
    borderRadius: 'var(--ci-radius)',
    fontSize:    14,
    color:       'var(--ci-text)',
    resize:      'none',
    lineHeight:  1.5,
    fontFamily:  'var(--ci-font)',
    outline:     'none',
    boxSizing:   'border-box',
  },
  sendBtn: {
    display:      'inline-flex',
    alignItems:   'center',
    gap:          6,
    padding:      '11px 18px',
    minHeight:    46,
    background:   'var(--ci-primary)',
    color:        '#fff',
    border:       'none',
    borderRadius: 'var(--ci-radius)',
    fontSize:     14,
    fontWeight:   600,
    cursor:       'pointer',
    flexShrink:   0,
    alignSelf:    'flex-end',
    fontFamily:   'var(--ci-font)',
  },
  thankYou: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    minHeight:      360,
    gap:            14,
    fontFamily:     'var(--ci-font)',
    textAlign:      'center',
    padding:        24,
  },
  thankYouIcon: {
    width:          56,
    height:         56,
    borderRadius:   '50%',
    background:     'var(--ci-primary-light)',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
  },
  tyTitle: { fontSize: 22, fontWeight: 700, color: 'var(--ci-text)', margin: 0, fontFamily: 'var(--ci-font)' },
  tySub:   { fontSize: 15, color: 'var(--ci-text-muted)', margin: 0, maxWidth: 360, lineHeight: 1.6, fontFamily: 'var(--ci-font)' },
};
